import { type UtmParameters, kUtmParameterKeys } from './UtmParameters';

/**
 * Campaign attribution captured from the landing URL and held for the rest of
 * the tab's session.
 *
 * A creator's ECHO Comment-to-DM link lands the buyer on the storefront with
 * `utm_*` params, but the purchase completes several navigations later on
 * `/checkout/success` — by which point the original query string is long gone.
 * Upstream PopshopRN kept them in a process-lifetime `BehaviorSubject`; the web
 * equivalent is `sessionStorage`, which spans the navigations AND survives the
 * off-site redirect-3DS round trip, while still dying with the tab (so a later
 * organic visit can't inherit someone else's attribution).
 *
 * Persisted AS a query string rather than JSON so the read path is the same
 * parser as the capture path — one code path, and no untyped `JSON.parse`
 * result to interrogate.
 */
const STORAGE_KEY = 'psl_utm_attribution';

/**
 * Pick the recognised `utm_*` params out of a query string. Pure; the ONE
 * conversion from raw query to {@link UtmParameters}, used by both capture and
 * read-back. A param present but empty is treated as absent.
 */
export function utmParametersFromQuery(search: string): UtmParameters {
  const params = new URLSearchParams(search);
  const utm: UtmParameters = {};
  for (const key of kUtmParameterKeys) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
  }
  return utm;
}

/** Serialise back to a query string for storage. Pure. */
function utmParametersToQuery(utm: UtmParameters): string {
  const params = new URLSearchParams();
  for (const key of kUtmParameterKeys) {
    const value = utm[key];
    if (value) {
      params.set(key, value);
    }
  }
  return params.toString();
}

/**
 * Capture the current URL's `utm_*` params. Call at the earliest client load on
 * every landing — a later navigation drops the query string.
 *
 * A URL carrying no recognised param leaves whatever was captured earlier
 * intact: the buyer navigating from the storefront to a product page must not
 * wipe the attribution that brought them in. Only a URL that DOES carry params
 * replaces it (a fresh campaign arrival supersedes the previous one).
 */
export function captureUtmParameters(): void {
  try {
    if (typeof window === 'undefined' || !window.location) return;
    const utm = utmParametersFromQuery(window.location.search);
    const query = utmParametersToQuery(utm);
    if (!query) return;
    sessionStorage.setItem(STORAGE_KEY, query);
  } catch {
    /* private browsing / storage disabled — attribution is best-effort */
  }
}

/** The attribution captured for this tab session; `{}` when there is none. */
export function readUtmParameters(): UtmParameters {
  try {
    const query = sessionStorage.getItem(STORAGE_KEY);
    if (!query) return {};
    return utmParametersFromQuery(query);
  } catch {
    return {};
  }
}
